import React, { useEffect } from 'react';
import { Sparkles, Shield, Clock, Award, CheckCircle, Star, Gift, Heart, Home as HomeIcon, Baby, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { testimonials, services } from '../mock';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { CountUp } from '../components/CountUp';
import { getCurrentStats } from '../utils/autoIncrementStats';
import config from '../config';
import { trackBookNow } from '../analytics';

export const Home = () => {
  const currentStats = getCurrentStats();

  useEffect(() => {
    // Load House Call Pro lead capture script
    const script = document.createElement('script');
    script.src = config.hcp.scriptUrl;
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const handleBookNow = () => {
    // Track Book Now click in Google Analytics
    trackBookNow('hero_section');
    
    // Redirect to House Call Pro lead form
    window.open(config.hcp.bookingUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section id="home" className="pt-[140px] sm:pt-[152px] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#28BCB7]/5 via-white to-[#2C60AC]/5"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fade-in">
              <div className="inline-block">
                <span className="bg-[#28BCB7]/10 text-[#2C60AC] px-4 py-2 rounded-full text-sm font-semibold">
                  Professional Cleaning Services
                </span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-[#303030] leading-tight">
                Tidy Space.
                <span className="text-[#28BCB7]"> Happy Mind!</span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                Experience the difference of professional cleaning services. We transform your space with meticulous attention to detail, eco-friendly products, and a commitment to excellence <span className="font-bold text-[#28BCB7] uppercase">to make your life easier.</span>
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={handleBookNow}
                  className="bg-[#28BCB7] hover:bg-[#65C6BA] text-white px-8 py-6 rounded-lg text-lg font-semibold transition-all duration-300 hover:shadow-xl hover:scale-105"
                >
                  Book Your Cleaning Now
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
                  className="border-2 border-[#2C60AC] text-[#2C60AC] hover:bg-[#2C60AC] hover:text-white px-8 py-6 rounded-lg text-lg font-semibold transition-all duration-300"
                >
                  View Services
                </Button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#2C60AC]">
                    <CountUp end={currentStats.clients} suffix="+" />
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Happy Clients</div>
                </div>
                
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#2C60AC]">
                    <CountUp end={currentStats.spaces} suffix="+" />
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Spaces Cleaned</div>
                </div>
                
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#2C60AC]">
                    <CountUp end={currentStats.years} suffix="+" />
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Years Experience</div>
                </div>
                
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#2C60AC]">
                    <CountUp end={currentStats.satisfaction} suffix="%" />
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Satisfaction Rate</div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#28BCB7]/20 to-[#2C60AC]/20 rounded-3xl transform rotate-3"></div>
              <img 
                src={config.assets.images.heroImage}
                alt="Professional Cleaning - Clean dining room" 
                className="relative rounded-3xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#303030] mb-4">
              Our <span className="text-[#28BCB7]">Services</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive cleaning solutions tailored to your needs. From homes to businesses, we've got you covered.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card key={service.id} className="group hover:shadow-xl transition-all duration-300 border-0 overflow-hidden animate-slide-up" style={{ animationDelay: `${index * 100}ms` }}>
                <CardContent className="p-6 space-y-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#28BCB7] to-[#65C6BA] rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <Sparkles className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#303030]">{service.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                  <ul className="space-y-2 pt-4">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <CheckCircle className="h-5 w-5 text-[#28BCB7] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button 
              onClick={handleBookNow}
              className="bg-[#2C60AC] hover:bg-[#28BCB7] text-white px-8 py-6 rounded-lg text-lg font-semibold transition-all duration-300 hover:shadow-xl"
            >
              Schedule Your Service Today
            </Button>
          </div>
        </div>
      </section>

      {/* Gift Card Section */}
      <section id="giftcard" className="py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#28BCB7]/5 via-white to-[#2C60AC]/5"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <Gift className="h-16 w-16 text-[#28BCB7] mx-auto" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#303030] mb-4">
              🎁 <span className="text-[#28BCB7]">Gift</span> the Perfect Present
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Give the gift of time, comfort, and a spotless home!
              <br />
              A professional cleaning is a thoughtful and practical gift for any special occasion.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Mother's Day */}
            <Card className="hover:shadow-xl transition-all duration-300 border-0">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#28BCB7] to-[#65C6BA] rounded-full flex items-center justify-center flex-shrink-0">
                    <Heart className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#303030] mb-2">Mother's Day</h3>
                    <p className="text-gray-600 text-sm">Surprise Mom with rest and well-being. Let her enjoy a spotless home without lifting a finger.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Father's Day */}
            <Card className="hover:shadow-xl transition-all duration-300 border-0">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#28BCB7] to-[#65C6BA] rounded-full flex items-center justify-center flex-shrink-0">
                    <Award className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#303030] mb-2">Father's Day</h3>
                    <p className="text-gray-600 text-sm">A useful gift that makes daily life easier. Dad will appreciate the extra time to relax.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Valentine's Day */}
            <Card className="hover:shadow-xl transition-all duration-300 border-0">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#28BCB7] to-[#65C6BA] rounded-full flex items-center justify-center flex-shrink-0">
                    <Heart className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#303030] mb-2">Valentine's Day</h3>
                    <p className="text-gray-600 text-sm">A gesture of love and care. Show you care with a clean, comfortable space.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Birthdays */}
            <Card className="hover:shadow-xl transition-all duration-300 border-0">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#28BCB7] to-[#65C6BA] rounded-full flex items-center justify-center flex-shrink-0">
                    <Gift className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#303030] mb-2">Birthdays</h3>
                    <p className="text-gray-600 text-sm">Show affection with a gift that makes a real difference in someone's daily life.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Weddings */}
            <Card className="hover:shadow-xl transition-all duration-300 border-0">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#28BCB7] to-[#65C6BA] rounded-full flex items-center justify-center flex-shrink-0">
                    <Heart className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#303030] mb-2">Weddings</h3>
                    <p className="text-gray-600 text-sm">Perfect for newlyweds starting their new life with a spotless home.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Housewarming */}
            <Card className="hover:shadow-xl transition-all duration-300 border-0">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#28BCB7] to-[#65C6BA] rounded-full flex items-center justify-center flex-shrink-0">
                    <HomeIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#303030] mb-2">Housewarming</h3>
                    <p className="text-gray-600 text-sm">The perfect present for someone starting fresh in a new home.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Christmas */}
            <Card className="hover:shadow-xl transition-all duration-300 border-0">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#28BCB7] to-[#65C6BA] rounded-full flex items-center justify-center flex-shrink-0">
                    <Gift className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#303030] mb-2">Christmas</h3>
                    <p className="text-gray-600 text-sm">A kind way to wish comfort and practicality during the holiday season.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* New Year */}
            <Card className="hover:shadow-xl transition-all duration-300 border-0">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#28BCB7] to-[#65C6BA] rounded-full flex items-center justify-center flex-shrink-0">
                    <Calendar className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#303030] mb-2">New Year</h3>
                    <p className="text-gray-600 text-sm">Start the year with a renewed and organized environment. Fresh start, clean slate!</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* After Renovation or Move */}
            <Card className="hover:shadow-xl transition-all duration-300 border-0">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#28BCB7] to-[#65C6BA] rounded-full flex items-center justify-center flex-shrink-0">
                    <Sparkles className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#303030] mb-2">After Renovation or Move</h3>
                    <p className="text-gray-600 text-sm">A useful and necessary gift to help settle into a clean, fresh space.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* New Baby Gift */}
            <Card className="hover:shadow-xl transition-all duration-300 border-0">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#28BCB7] to-[#65C6BA] rounded-full flex items-center justify-center flex-shrink-0">
                    <Baby className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#303030] mb-2">New Baby Gift</h3>
                    <p className="text-gray-600 text-sm">Offer rest and peace to new parents. They'll cherish the extra time with their newborn.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* International Women's Day */}
            <Card className="hover:shadow-xl transition-all duration-300 border-0">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#28BCB7] to-[#65C6BA] rounded-full flex items-center justify-center flex-shrink-0">
                    <Heart className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#303030] mb-2">International Women's Day</h3>
                    <p className="text-gray-600 text-sm">Celebrate with a gesture of care and appreciation. Honor the special women in your life.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Thanksgiving */}
            <Card className="hover:shadow-xl transition-all duration-300 border-0">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#28BCB7] to-[#65C6BA] rounded-full flex items-center justify-center flex-shrink-0">
                    <Heart className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#303030] mb-2">Thanksgiving</h3>
                    <p className="text-gray-600 text-sm">Show gratitude with a gift that brings relief and comfort during the busy holiday season.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <Button 
              onClick={handleBookNow}
              className="bg-[#28BCB7] hover:bg-[#65C6BA] text-white px-10 py-6 rounded-lg text-lg font-bold transition-all duration-300 hover:shadow-xl hover:scale-105"
            >
              <Gift className="h-5 w-5 mr-2 inline" />
              Purchase a Gift Card
            </Button>
            <p className="mt-4 text-gray-600">Contact us to arrange your personalized gift card!</p>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#303030] mb-4">
              Why Choose <span className="text-[#28BCB7]">Up Cleaning Pro</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We're committed to delivering exceptional cleaning services that exceed your expectations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-6 group hover:bg-gray-50 rounded-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-[#28BCB7] to-[#65C6BA] rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#303030] mb-2">Trusted & Insured</h3>
              <p className="text-gray-600">Fully licensed, bonded, and insured for your peace of mind.</p>
            </div>

            <div className="text-center p-6 group hover:bg-gray-50 rounded-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-[#28BCB7] to-[#65C6BA] rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <Clock className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#303030] mb-2">Flexible Scheduling</h3>
              <p className="text-gray-600">Book online 24/7 and choose times that work for you.</p>
            </div>

            <div className="text-center p-6 group hover:bg-gray-50 rounded-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-[#28BCB7] to-[#65C6BA] rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <Sparkles className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#303030] mb-2">Eco-Friendly</h3>
              <p className="text-gray-600">Safe, non-toxic cleaning products for your family and pets.</p>
            </div>

            <div className="text-center p-6 group hover:bg-gray-50 rounded-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-[#28BCB7] to-[#65C6BA] rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <Award className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#303030] mb-2">Satisfaction Guaranteed</h3>
              <p className="text-gray-600">We're not happy until you're thrilled with the results.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#303030] mb-4">
              What Our <span className="text-[#28BCB7]">Clients Say</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Don't just take our word for it. See what our satisfied customers have to say.
            </p>
            <div className="mt-6">
              <p className="text-lg font-semibold text-[#2C60AC]">⭐ 4.9/5 Average Rating • 700+ Happy Clients</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={testimonial.id} className="hover:shadow-xl transition-all duration-300 border-0 animate-slide-up" style={{ animationDelay: `${index * 100}ms` }}>
                <CardContent className="p-6 space-y-4">
                  <div className="flex space-x-1">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-[#28BCB7] text-[#28BCB7]" />
                    ))}
                  </div>
                  <p className="text-gray-700 leading-relaxed">"{testimonial.text}"</p>
                  <div className="pt-4 border-t border-gray-100">
                    <p className="font-semibold text-[#303030]">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.location}</p>
                    <p className="text-sm text-[#28BCB7] mt-1">{testimonial.service}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#2C60AC] to-[#28BCB7] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '30px 30px' }}></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready for a Cleaner, Fresher Space?
          </h2>
          <p className="text-xl mb-8 opacity-95">
            Book your professional cleaning service today and experience the Up Cleaning Pro difference. Easy online scheduling in just minutes!
          </p>
          <Button 
            onClick={handleBookNow}
            className="bg-white text-[#2C60AC] hover:bg-gray-100 px-10 py-6 rounded-lg text-lg font-bold transition-all duration-300 hover:shadow-2xl hover:scale-105"
          >
            Book Your Cleaning Now
          </Button>
          <p className="mt-6 text-sm opacity-90">Instant confirmation • Flexible scheduling • 100% satisfaction guaranteed</p>
        </div>
      </section>

      <Footer />
    </div>
  );
};